import type { ValueOf } from '@kc-console/core';
import type { IconButtonGhostMiniProps } from '../IconButtonGhostMini';
import type { PopoverProps } from '../Popover';
import type { PopoverContentProps } from '../PopoverContent';
import type { TooltipProps } from '../Tooltip';

const InfoButtonAlertTypeSet = ['tooltip', 'popover'] as const;
const InfoButtonColorSet = ['default', 'red', 'yellow'] as const;
const InfoButtonSizeSet = ['sm', 'md'] as const;
const InfoButtonArrowSet = ['down', 'up', 'left', 'right'] as const;
const InfoButtonIconSet = ['information', 'question', 'error'] as const;

type InfoButtonAlertType = ValueOf<typeof InfoButtonAlertTypeSet>;
type InfoButtonColor = ValueOf<typeof InfoButtonColorSet>;
type InfoButtonSize = ValueOf<typeof InfoButtonSizeSet>;
type InfoButtonArrow = ValueOf<typeof InfoButtonArrowSet>;
type InfoButtonIcon = ValueOf<typeof InfoButtonIconSet>;

type ArrowToPlacement = {
  down: 'top';
  left: 'right';
  right: 'left';
  up: 'bottom';
};

const ARROW_TO_PLACEMENT_MAP: ArrowToPlacement = {
  down: 'top',
  up: 'bottom',
  left: 'right',
  right: 'left',
} as const;

interface InfoButtonBaseProps {
  /**
   * InfoButton 아이콘의 유형을 지정합니다.
   * information: IcInformationCircle
   * question: IcQuestionCircle
   * error: IcErrorCircle
   * @default "information"
   */
  icon?: InfoButtonIcon;
}

interface InfoButtonSharedProps
  extends Omit<IconButtonGhostMiniProps, 'size' | 'color' | 'type' | 'onClick'> {
  /**
   * 툴팁 또는 팝오버가 표시될 방향을 지정합니다.
   * 이 값에 따라 placement가 자동으로 계산됩니다.
   * - down: 툴팁/팝오버가 위쪽에 표시됨 (placement: 'top')
   * - up: 툴팁/팝오버가 아래쪽에 표시됨 (placement: 'bottom')
   * - left: 툴팁/팝오버가 오른쪽에 표시됨 (placement: 'right')
   * - right: 툴팁/팝오버가 왼쪽에 표시됨 (placement: 'left')
   */
  arrow?: InfoButtonArrow;
  /**
   * InfoButton 아이콘의 색상을 지정합니다.
   * @default "default"
   */
  color?: InfoButtonColor;

  /**
   * 아이콘과 툴팁의 스타일을 반전시킵니다.
   * true일 경우 fill 아이콘을 사용하고, 툴팁의 배경색과 텍스트 색상이 반전됩니다.
   * @default false
   */
  inverse?: boolean;

  /**
   * InfoButton 아이콘의 크기를 설정합니다.
   * sm: 12 x 12 / md: 16 x 16
   * @default "sm"
   */
  size?: InfoButtonSize;
}

type InfoButtonWithTooltipProps = {
  /**
   * InfoButton을 호버해서 표시할 툴팁의 컨텐츠를 설정합니다.
   * arrow 속성에 따라 placement가 자동으로 설정됩니다.
   */
  tooltipProps: Omit<TooltipProps, 'children' | 'placement'>;
} & InfoButtonSharedProps;

type InfoButtonWithPopoverProps = {
  /**
   * InfoButton을 클릭해서 표시할 팝오버의 컨텐츠를 설정합니다.
   * popoverProps의 placement 속성과 popoverContentProps의 placement 속성은 일치해야 합니다.
   * arrow 속성에 따라 placement가 자동으로 설정됩니다.
   */
  popoverContentProps: Omit<PopoverContentProps, 'placement'>;
  /**
   * InfoButton을 클릭해서 표시할 팝오버를 설정합니다. id, open, anchorEl, onClose 요소는 전달하지 않습니다.
   */
  popoverProps: Omit<PopoverProps, 'id' | 'open' | 'anchorEl' | 'onClose' | 'placement'>;
} & InfoButtonSharedProps;

type InfoButtonProps = InfoButtonBaseProps &
  (
    | ({
        /**
         * alertType 속성을 설정하지 않거나 (undefined) tooltip 으로 지정할 경우, InfoButtonWithTooltipProps 인터페이스를 상속받습니다.
         * InfoButtonWithTooltip 컴포넌트는 tooltipProps 속성을 필수로 설정해야 합니다.
         *
         */
        alertType?: 'tooltip';
      } & InfoButtonWithTooltipProps)
    | ({
        /**
         * alertType 속성을 popover로 설정할 경우, InfoButtonWithPopoverProps 인터페이스를 상속받습니다.
         * InfoButtonWithPopover 컴포넌트는 popoverContentProps, popoverProps 속성을 필수로 설정해야 합니다.
         */
        alertType: 'popover';
      } & InfoButtonWithPopoverProps)
  );

export {
  InfoButtonAlertTypeSet,
  InfoButtonColorSet,
  InfoButtonSizeSet,
  InfoButtonArrowSet,
  ARROW_TO_PLACEMENT_MAP,
  InfoButtonIconSet,
};

export type {
  InfoButtonProps,
  InfoButtonProps as InfoButtonOwnerState,
  InfoButtonAlertType,
  InfoButtonWithPopoverProps,
  InfoButtonWithTooltipProps,
  InfoButtonArrow,
  InfoButtonColor,
  InfoButtonSize,
  InfoButtonIcon,
};
